import { useState, useEffect } from 'react'
import type { LabClient } from '@alpha-edge/shared'
import { Modal, FormField, btnPrimary, btnSecondary, colors, input } from '../../components/ui'
import { api, ApiError } from '../../services/api'

export interface FacilityEntry {
  labClientId?: string
  sendingFacility?: string
  receivingFacility?: string
  overrides?: {
    transport?: Record<string, unknown>
    hl7?: Record<string, unknown>
    codeMappings?: Record<string, unknown>
  }
  // legacy pass-through fields preserved on save so existing configs don't lose data
  [key: string]: unknown
}

interface FacilityEditModalProps {
  readonly facility: FacilityEntry | null
  readonly isNew: boolean
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onSave: (facility: FacilityEntry) => void
}

interface FacilityFormProps {
  readonly facility: FacilityEntry | null
  readonly onSave: (facility: FacilityEntry) => void
  readonly onClose: () => void
}

interface CodeMappingRow {
  readonly localCode: string
  readonly externalCode: string
  readonly externalName: string
}

const OVERRIDES_PLACEHOLDER = `{
  "transport": { "host": "sftp.client-a.example.com" },
  "hl7": {
    "segments": {
      "PID": { "nameFieldPosition": 6 }
    }
  },
  "codeMappings": {
    "testCodes": { "LOCAL1": { "code": "EXT-1", "name": "Test 1" } }
  }
}`

const OBX_VALUE_TYPES = ['', 'NM', 'ST', 'CE', 'TX'] as const

// --- pure helpers ---

function parseOverridesText(text: string): Record<string, unknown> | null {
  const trimmed = text.trim()
  if (!trimmed || trimmed === '{}') return {}
  try {
    const parsed: unknown = JSON.parse(trimmed)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    return parsed as Record<string, unknown>
  } catch {
    return null
  }
}

function getNestedStr(obj: Record<string, unknown>, path: readonly string[]): string {
  const val = path.reduce<unknown>((acc, key) => {
    if (acc !== null && typeof acc === 'object' && !Array.isArray(acc)) {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
  return typeof val === 'string' ? val : typeof val === 'number' ? String(val) : ''
}

/**
 * Immutably set (or remove) a value at a dotted path within a nested object.
 * An empty string / null / undefined value removes the key.
 * Empty intermediate objects are pruned automatically.
 */
function setNestedPath(obj: Record<string, unknown>, path: readonly string[], value: unknown): Record<string, unknown> {
  if (path.length === 0) return obj
  const [first, ...rest] = path
  if (rest.length === 0) {
    if (value === undefined || value === null || value === '') {
      return Object.fromEntries(Object.entries(obj).filter(([k]) => k !== first))
    }
    return { ...obj, [first]: value }
  }
  const child = (obj[first] as Record<string, unknown> | undefined) ?? {}
  const updatedChild = setNestedPath(child, rest, value)
  if (Object.keys(updatedChild).length === 0) {
    return Object.fromEntries(Object.entries(obj).filter(([k]) => k !== first))
  }
  return { ...obj, [first]: updatedChild }
}

function extractCodeMappingRows(parsed: Record<string, unknown> | null): readonly CodeMappingRow[] {
  if (!parsed) return []
  const codeMappings = parsed.codeMappings as Record<string, unknown> | undefined
  const testCodes = codeMappings?.testCodes as Record<string, unknown> | undefined
  if (!testCodes) return []
  return Object.entries(testCodes).map(([localCode, entry]) => {
    const e = entry as Record<string, unknown> | null | undefined
    return {
      localCode,
      externalCode: typeof e?.code === 'string' ? e.code : '',
      externalName: typeof e?.name === 'string' ? e.name : '',
    }
  })
}

function buildTestCodesObject(rows: readonly CodeMappingRow[]): Record<string, { code: string; name: string }> {
  const result: Record<string, { code: string; name: string }> = {}
  for (const row of rows) {
    if (row.localCode.trim()) {
      result[row.localCode] = { code: row.externalCode, name: row.externalName }
    }
  }
  return result
}

// --- components ---

export function FacilityEditModal({ facility, isNew, isOpen, onClose, onSave }: FacilityEditModalProps) {
  const title = isNew ? 'New Facility Override' : 'Edit Facility Override'
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} width="680px">
      {isOpen && <FacilityForm facility={facility} onSave={onSave} onClose={onClose} />}
    </Modal>
  )
}

function FacilityForm({ facility, onSave, onClose }: FacilityFormProps) {
  const [labClientId, setLabClientId] = useState(facility?.labClientId ?? '')
  const [sendingFacility, setSendingFacility] = useState(facility?.sendingFacility ?? '')
  const [receivingFacility, setReceivingFacility] = useState(facility?.receivingFacility ?? '')

  const initialOverridesText = facility?.overrides ? JSON.stringify(facility.overrides, null, 2) : '{}'
  const [overridesText, setOverridesText] = useState(initialOverridesText)

  const [labClients, setLabClients] = useState<readonly LabClient[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Structured HL7 fields — initialized once from parsed overrides on mount.
  // overridesText is the authoritative source; these push updates into it.
  const [pidNameFieldPos, setPidNameFieldPos] = useState(() =>
    getNestedStr(parseOverridesText(initialOverridesText) ?? {}, ['hl7', 'segments', 'PID', 'nameFieldPosition']),
  )
  const [mshSendingApp, setMshSendingApp] = useState(() =>
    getNestedStr(parseOverridesText(initialOverridesText) ?? {}, ['hl7', 'segments', 'MSH', 'sendingApplication']),
  )
  const [mshSendFacility, setMshSendFacility] = useState(() =>
    getNestedStr(parseOverridesText(initialOverridesText) ?? {}, ['hl7', 'segments', 'MSH', 'sendingFacility']),
  )
  const [obxValueType, setObxValueType] = useState(() =>
    getNestedStr(parseOverridesText(initialOverridesText) ?? {}, ['hl7', 'segments', 'OBX', 'valueType']),
  )
  const [codeMappingRows, setCodeMappingRows] = useState<readonly CodeMappingRow[]>(() =>
    extractCodeMappingRows(parseOverridesText(initialOverridesText)),
  )

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly LabClient[]>('/lab-clients?limit=100')
      .then((res) => {
        if (cancelled) return
        setLabClients(res.data ?? [])
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof ApiError ? err.message : 'Failed to load lab clients')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Validity + derived warnings
  const jsonIsValid = parseOverridesText(overridesText) !== null

  const pidPos = pidNameFieldPos === '' ? NaN : parseInt(pidNameFieldPos, 10)
  const pidPosWarning =
    !isNaN(pidPos) && pidPos !== 5 && pidPos !== 6
      ? 'Non-standard PID name field position — verify against EHR spec.'
      : null

  const seenCodes = new Set<string>()
  const hasDuplicateCode = codeMappingRows.some((row) => {
    if (!row.localCode.trim()) return false
    if (seenCodes.has(row.localCode)) return true
    seenCodes.add(row.localCode)
    return false
  })

  // Warning: user removed MSH from hl7.segments via raw JSON edit
  const mshRemovedWarning = (() => {
    const parsed = parseOverridesText(overridesText)
    if (!parsed || !jsonIsValid) return null
    const hl7 = parsed.hl7 as Record<string, unknown> | undefined
    if (!hl7) return null
    const segments = hl7.segments as Record<string, unknown> | undefined
    if (!segments || Object.keys(segments).length === 0) return null
    return !('MSH' in segments)
      ? 'MSH segment override removed — this is nominally required; verify against EHR spec.'
      : null
  })()

  // Applies an immutable transform to the parsed overrides and re-serialises.
  // No-ops when the current JSON is invalid.
  function applyToOverrides(updater: (parsed: Record<string, unknown>) => Record<string, unknown>) {
    const parsed = parseOverridesText(overridesText)
    if (parsed === null) return
    const updated = updater(parsed)
    setOverridesText(JSON.stringify(updated, null, 2))
  }

  function handleHl7FieldChange(path: readonly string[], rawValue: string, asNumber = false) {
    if (asNumber) {
      if (rawValue === '') {
        applyToOverrides((p) => setNestedPath(p, path, ''))
      } else {
        const num = parseInt(rawValue, 10)
        if (!isNaN(num)) {
          applyToOverrides((p) => setNestedPath(p, path, num))
        }
      }
    } else {
      applyToOverrides((p) => setNestedPath(p, path, rawValue))
    }
  }

  function syncCodeMappingsToOverrides(rows: readonly CodeMappingRow[]) {
    const testCodes = buildTestCodesObject(rows)
    applyToOverrides((p) => {
      if (Object.keys(testCodes).length === 0) {
        return setNestedPath(p, ['codeMappings', 'testCodes'], '')
      }
      return setNestedPath(p, ['codeMappings', 'testCodes'], testCodes)
    })
  }

  function handleCodeMappingChange(index: number, field: keyof CodeMappingRow, value: string) {
    const newRows = codeMappingRows.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    setCodeMappingRows(newRows)
    syncCodeMappingsToOverrides(newRows)
  }

  function handleAddCodeMappingRow() {
    // Blank row — does not write to JSON until localCode is filled
    setCodeMappingRows([...codeMappingRows, { localCode: '', externalCode: '', externalName: '' }])
  }

  function handleRemoveCodeMappingRow(index: number) {
    const newRows = codeMappingRows.filter((_, i) => i !== index)
    setCodeMappingRows(newRows)
    syncCodeMappingsToOverrides(newRows)
  }

  function handleSave() {
    setValidationError(null)

    let overrides: FacilityEntry['overrides'] | undefined
    const trimmed = overridesText.trim()
    if (trimmed && trimmed !== '{}') {
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          setValidationError('Overrides must be a JSON object')
          return
        }
        overrides = parsed as FacilityEntry['overrides']
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setValidationError(`Invalid JSON: ${message}`)
        return
      }
    }

    if (!labClientId && !sendingFacility && !receivingFacility) {
      setValidationError('At least one of lab client, sending facility, or receiving facility is required')
      return
    }

    const next: FacilityEntry = { ...(facility ?? {}) }
    if (labClientId) next.labClientId = labClientId
    else delete next.labClientId
    if (sendingFacility) next.sendingFacility = sendingFacility
    else delete next.sendingFacility
    if (receivingFacility) next.receivingFacility = receivingFacility
    else delete next.receivingFacility
    if (overrides) next.overrides = overrides
    else delete next.overrides

    onSave(next)
    onClose()
  }

  const selectStyle = { ...input, width: '100%' }

  const fieldLabel = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: '0.25rem',
    color: colors.text,
  } as const

  const warningText = {
    fontSize: '0.75rem',
    color: colors.warning,
    marginTop: '0.25rem',
  } as const

  const errorDivStyle = {
    padding: '0.75rem',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#dc2626',
    marginBottom: '0.75rem',
  } as const

  const disabledBanner = !jsonIsValid ? (
    <div style={{ fontSize: '0.75rem', color: colors.danger, marginBottom: '0.5rem' }}>
      Advanced JSON is invalid — fix it below before using structured editors.
    </div>
  ) : null

  const thStyle = {
    textAlign: 'left' as const,
    padding: '0.25rem 0.5rem 0.375rem',
    color: colors.textMuted,
    fontWeight: 600,
    fontSize: '0.8125rem',
  }

  const cellStyle = { padding: '0.25rem 0.5rem' } as const

  return (
    <div>
      {loadError && <div style={errorDivStyle}>{loadError}</div>}
      {validationError && <div style={errorDivStyle}>{validationError}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {/* Identity */}
        <div>
          <label style={fieldLabel}>Lab Client</label>
          <select style={selectStyle} value={labClientId} onChange={(e) => setLabClientId(e.target.value)}>
            <option value="">-- none (match by sending/receiving facility) --</option>
            {labClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} - {c.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <FormField
            name="sendingFacility"
            label="Sending Facility"
            value={sendingFacility}
            onChange={setSendingFacility}
            placeholder="e.g., ALPHALAB"
          />
          <FormField
            name="receivingFacility"
            label="Receiving Facility"
            value={receivingFacility}
            onChange={setReceivingFacility}
            placeholder="e.g., CLINIC-A"
          />
        </div>

        {/* HL7 Segment Overrides */}
        <div>
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              paddingBottom: '0.375rem',
              borderBottom: `1px solid ${colors.border}`,
              marginBottom: '0.5rem',
            }}
          >
            HL7 Segment Overrides
          </div>
          {disabledBanner}
          {mshRemovedWarning && <div style={warningText}>{mshRemovedWarning}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div>
              <label style={fieldLabel}>PID Name Field Position</label>
              <input
                type="number"
                style={{ ...input, width: '120px' }}
                value={pidNameFieldPos}
                disabled={!jsonIsValid}
                placeholder="5 or 6"
                onChange={(e) => {
                  setPidNameFieldPos(e.target.value)
                  handleHl7FieldChange(['hl7', 'segments', 'PID', 'nameFieldPosition'], e.target.value, true)
                }}
              />
              {pidPosWarning && <div style={warningText}>{pidPosWarning}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={fieldLabel}>MSH Sending Application</label>
                <input
                  type="text"
                  style={{ ...input, width: '100%' }}
                  value={mshSendingApp}
                  disabled={!jsonIsValid}
                  placeholder="e.g., ALPHAEDGE"
                  onChange={(e) => {
                    setMshSendingApp(e.target.value)
                    handleHl7FieldChange(['hl7', 'segments', 'MSH', 'sendingApplication'], e.target.value)
                  }}
                />
              </div>
              <div>
                <label style={fieldLabel}>MSH Sending Facility</label>
                <input
                  type="text"
                  style={{ ...input, width: '100%' }}
                  value={mshSendFacility}
                  disabled={!jsonIsValid}
                  placeholder="e.g., MYLAB"
                  onChange={(e) => {
                    setMshSendFacility(e.target.value)
                    handleHl7FieldChange(['hl7', 'segments', 'MSH', 'sendingFacility'], e.target.value)
                  }}
                />
              </div>
            </div>

            <div>
              <label style={fieldLabel}>OBX Value Type</label>
              <select
                style={{ ...selectStyle, width: '160px' }}
                value={obxValueType}
                disabled={!jsonIsValid}
                onChange={(e) => {
                  setObxValueType(e.target.value)
                  handleHl7FieldChange(['hl7', 'segments', 'OBX', 'valueType'], e.target.value)
                }}
              >
                {OBX_VALUE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t === '' ? '-- default --' : t}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Code Mappings */}
        <div>
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              paddingBottom: '0.375rem',
              borderBottom: `1px solid ${colors.border}`,
              marginBottom: '0.5rem',
            }}
          >
            Code Mappings
          </div>
          {disabledBanner}
          {hasDuplicateCode && <div style={warningText}>Duplicate local code — last entry wins on save.</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={thStyle}>Local Code</th>
                <th style={thStyle}>External Code</th>
                <th style={thStyle}>External Name</th>
                <th style={{ width: '40px' }} />
              </tr>
            </thead>
            <tbody>
              {codeMappingRows.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      padding: '0.5rem',
                      color: colors.textMuted,
                      fontSize: '0.8125rem',
                      fontStyle: 'italic',
                    }}
                  >
                    No mappings — click Add Row to define test code translations.
                  </td>
                </tr>
              )}
              {codeMappingRows.map((row, i) => (
                <tr key={i}>
                  <td style={cellStyle}>
                    <input
                      type="text"
                      aria-label="Local code"
                      style={{ ...input, height: '32px', fontSize: '0.8125rem' }}
                      value={row.localCode}
                      disabled={!jsonIsValid}
                      placeholder="LOCAL1"
                      onChange={(e) => handleCodeMappingChange(i, 'localCode', e.target.value)}
                    />
                  </td>
                  <td style={cellStyle}>
                    <input
                      type="text"
                      aria-label="External code"
                      style={{ ...input, height: '32px', fontSize: '0.8125rem' }}
                      value={row.externalCode}
                      disabled={!jsonIsValid}
                      placeholder="EXT-1"
                      onChange={(e) => handleCodeMappingChange(i, 'externalCode', e.target.value)}
                    />
                  </td>
                  <td style={cellStyle}>
                    <input
                      type="text"
                      aria-label="External name"
                      style={{ ...input, height: '32px', fontSize: '0.8125rem' }}
                      value={row.externalName}
                      disabled={!jsonIsValid}
                      placeholder="Test 1"
                      onChange={(e) => handleCodeMappingChange(i, 'externalName', e.target.value)}
                    />
                  </td>
                  <td style={{ padding: '0.25rem 0.25rem', textAlign: 'center' }}>
                    <button
                      type="button"
                      aria-label="Remove row"
                      disabled={!jsonIsValid}
                      onClick={() => handleRemoveCodeMappingRow(i)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: colors.danger,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        lineHeight: 1,
                      }}
                    >
                      X
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '0.5rem' }}>
            <button
              type="button"
              disabled={!jsonIsValid}
              onClick={handleAddCodeMappingRow}
              style={{ ...btnSecondary, fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
            >
              + Add Row
            </button>
          </div>
        </div>

        {/* Advanced JSON */}
        <div>
          <label style={fieldLabel}>Advanced overrides (JSON)</label>
          <textarea
            data-testid="overrides-textarea"
            style={{
              ...input,
              width: '100%',
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              minHeight: '160px',
              resize: 'vertical',
              height: 'auto',
            }}
            value={overridesText}
            onChange={(e) => setOverridesText(e.target.value)}
            placeholder={OVERRIDES_PLACEHOLDER}
          />
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.25rem' }}>
            Partial deep-merge on top of EHR defaults. Keys: transport, hl7, codeMappings.
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.5rem',
          marginTop: '1.25rem',
          paddingTop: '0.75rem',
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <button type="button" onClick={onClose} style={btnSecondary}>
          Cancel
        </button>
        <button type="button" onClick={handleSave} style={btnPrimary}>
          Save
        </button>
      </div>
    </div>
  )
}
